/**
 * Servicio para flujo de login con Google: crear/actualizar usuarios y solicitudes.
 */
import { doc, setDoc, updateDoc, addDoc, collection, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { matchEmpresaByRutWeb } from './onboardingUserService';

const USUARIOS = 'usuarios';
const SOLICITUDES_CLIENTE = 'solicitudes_cliente';
const SOLICITUDES_EMPRESA = 'solicitudes_empresa';

/**
 * Crea usuarios/{uid} para primer login con Google si no existe.
 * @param {{ uid: string, email: string, displayName?: string }} googleUser
 * @returns {{ created: boolean, userDoc?: import('firebase/firestore').DocumentSnapshot }}
 */
export async function ensureUserDocForGoogle(googleUser) {
  const uid = googleUser.uid;
  const ref = doc(db, USUARIOS, uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, {
      lastLoginAt: serverTimestamp(),
      authProvider: 'google',
    });
    return { created: false, userDoc: snap };
  }
  await setDoc(ref, {
    uid,
    email: (googleUser.email || '').trim().toLowerCase(),
    displayName: (googleUser.displayName || googleUser.email || '').trim(),
    authProvider: 'google',
    status: 'pendiente',
    rol: 'pendiente_tipo',
    accountType: 'pendiente_tipo',
    createdAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
  });
  return { created: true };
}

/**
 * Establece tipo Cliente: actualiza usuarios, crea solicitudes_cliente, redirige a /pendiente.
 */
export async function setAccountTypeCliente(uid, { email, nombre, telefono } = {}) {
  const userRef = doc(db, USUARIOS, uid);
  await updateDoc(userRef, {
    rol: 'cliente',
    accountType: 'cliente',
    status: 'pendiente',
    ...(nombre && { displayName: nombre }),
    lastLoginAt: serverTimestamp(),
  });

  const solicitudData = {
    uid,
    email: (email || '').trim().toLowerCase(),
    nombre: (nombre || '').trim() || null,
    telefono: (telefono || '').trim() || null,
    status: 'pendiente',
    createdAt: serverTimestamp(),
    tipo_registro: 'google',
  };
  const docRef = await addDoc(collection(db, SOLICITUDES_CLIENTE), solicitudData);
  return { solicitudId: docRef.id };
}

/**
 * Establece tipo Empresa: actualiza usuarios, crea solicitudes_empresa con match opcional.
 */
export async function setAccountTypeEmpresa(uid, { email, rutEmpresa, paginaWeb, razonSocial, telefono } = {}) {
  const match = await matchEmpresaByRutWeb(rutEmpresa, paginaWeb);

  const userRef = doc(db, USUARIOS, uid);
  const updateData = {
    rol: 'empresa',
    accountType: 'empresa',
    status: 'pendiente',
    displayName: (razonSocial || email || '').trim(),
    lastLoginAt: serverTimestamp(),
    ...(match.empresaId && { empresaIdCandidate: match.empresaId }),
  };
  await updateDoc(userRef, updateData);

  const solicitudData = {
    uid,
    email: (email || '').trim().toLowerCase(),
    rutEmpresa: (rutEmpresa || '').trim() || null,
    paginaWeb: (paginaWeb || '').trim() || null,
    razonSocial: (razonSocial || '').trim() || null,
    telefono: (telefono || '').trim() || null,
    status: 'pendiente',
    createdAt: serverTimestamp(),
    tipo_registro: 'google',
    ...(match.empresaId && { empresaIdSugerida: match.empresaId, matchScore: match.matchScore }),
  };
  const docRef = await addDoc(collection(db, SOLICITUDES_EMPRESA), solicitudData);
  return { solicitudId: docRef.id, empresaIdCandidate: match.empresaId };
}
